import { GWElement } from 'elements'
import { reduceObject, arrayToDict, toCamel, fromCamel, getBaseProductTitle, renderBanner } from 'utilities'

export class LineItem extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.getData()
  }

  disconnectedCallback() {
    this.setData()
  }

  getData() {
    const dataString = this.getAttribute('data')
    if (!dataString) {
      console.error('No data found on line item')
      return
    }
    
    this.data = reduceObject(JSON.parse(dataString), (acc, [key, value]) => ({ ...acc, [toCamel(key)]: value }))
    this.removeAttribute('data')
  }

  setData() {
    const jsonData = JSON.stringify(reduceObject(this.data, (acc, [key, value]) => ({ ...acc, [fromCamel(key)]: value })))
    this.setAttribute('data', jsonData)
    this.data = undefined
  }

  get key() {
    return this.getAttribute('key')
  }

  get bundleId() {
    return this.getAttribute('bundle-id')
  }

  get options() {
    const { optionsWithValues } = this.data ?? {}
    if (!optionsWithValues) return null

    return arrayToDict(optionsWithValues, { key: 'name', keyTransform: toCamel })
  }

  get variant() {
    const { id, productTitle } = this.data
    const title = getBaseProductTitle(productTitle)
    const options = this.options

    return { id, title, options }
  }
}
if (!window.customElements.get('line-item')) {
  window.customElements.define('line-item', LineItem)
}

export class LineItemBundle extends GWElement {
  constructor() {
    super()
    // TODO: Switch to simple "this.removeButton" since edit button is not needed
    this.buttons = {
      edit: this.querySelector('[data-action="edit"]'),
      remove: this.querySelector('[data-action="remove"]'),
    }

    // this.addListener(this.buttons.edit, 'click', this.editBundle)
    this.addListener(this.buttons.remove, 'click', this.onRemoveBundle)
  }

  defaults() {
    return {
      context: 'drawer'
    }
  }

  parsedCallback() {
    super.parsedCallback()
  }

  connectedCallback() {
    super.connectedCallback()

    if (!this.initialized && this.destroyed) {
      this.destroyed = false
      this.init()
    }

    setTimeout(this.validateBundle.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  destroy() {
    super.destroy()
    this.destroyed = true
  }

  get bundleId() {
    return this.getAttribute('bundle-id')
  }

  get bundleName() {
    return this.getAttribute('bundle-name')
  }

  get componentLineItems() {
    return [...document.querySelectorAll(`line-item[bundle-id="${this.bundleId}"]`)].reduce((acc, item) => acc.find(i => i.key === item.key) ? acc : [...acc, item], [])
  }

  get componentKeys() {
    return this.componentLineItems.map(item => item.key)
  }

  get components() {
    return this.componentLineItems.map(item => item.variant)
  }

  get componentIds() {
    return JSON.parse(this.getAttribute('component-ids'))
  }

  get invalidBundleMessage() {
    return window.themeVariables.strings.bundleInvalid.replace('[bundle]', this.bundleName)
  }

  onRemoveBundle(e) {
    e.preventDefault()
    this.removeBundle()
  }

  validateBundle() {
    const componentIds = this.componentIds
    const activeIds = this.components.map(variant => variant.id)
    const isValid = componentIds.length === activeIds.length && activeIds.every(id => componentIds.includes(id))

    if (!isValid) {
      this.removeBundle(true, () => renderBanner(this.invalidBundleMessage, 'error', { duration: 5000 }))
    }
  }

  async removeBundle(dispatch = true, callback) {
    const updates = this.componentKeys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {})

    // TODO: Abstract this for reuse (also in bundle-picker.js) - allow passing cart event and data
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }))
    let sectionsToBundle = []
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }))

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, sections: sectionsToBundle.join(",") }),
      keepalive: true,
    })
    const data = await response.json()
    
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }))
    if (!response.ok) {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: data.description
        }
      }));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      return false
    }

    this.dispatchEvent(new CustomEvent("bundle:remove", {
      bubbles: true,
      detail: { items: data.hasOwnProperty("items") ? data["items"] : [data] }
    }));
    
    if (dispatch) {
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:remove",
          cart: data
        }
      }));
    }

    if (callback) {
      callback()
    }

    if (this.options.context === 'page') {
      window.location.reload()
    }

    return data
  }
}
if (!window.customElements.get('line-item-bundle')) {
  window.customElements.define('line-item-bundle', LineItemBundle)
}
